import Icon, { IconsVariant } from "@/Components/Icon/Icon";
import { cn } from "@/shared/utils/cn";
import { ReactNode } from "react";

interface SettingsCardProps {
  title?: string;
  description?: string;
  icon?: IconsVariant;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  "data-tour"?: string;
}

/** A titled white card that groups related settings. */
const SettingsCard = ({
  title,
  description,
  icon,
  action,
  children,
  className,
  "data-tour": dataTour,
}: SettingsCardProps) => (
  <section
    data-tour={dataTour}
    className={cn(
      "rounded-2xl border border-grey-blue-light bg-white p-6 md:p-8",
      className
    )}
  >
    {title && (
      <div className="mb-6 flex items-start gap-4 border-b border-grey-blue-light pb-6">
        {icon && (
          <div className="flex size-14 shrink-0 items-center justify-center rounded-xl bg-primary/10">
            <Icon className="size-7 fill-primary" variants={icon} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h2 className="m-0 text-[1.8rem] leading-tight font-semibold">
            {title}
          </h2>
          {description && (
            <p className="mt-1 mb-0 text-small text-text-tertiary">
              {description}
            </p>
          )}
        </div>
        {action}
      </div>
    )}
    {children}
  </section>
);

interface SettingsFieldProps {
  label: string;
  hint?: string;
  children: ReactNode;
  className?: string;
  "data-tour"?: string;
}

/** A label (with optional hint) stacked above its control. */
export const SettingsField = ({
  label,
  hint,
  children,
  className,
  "data-tour": dataTour,
}: SettingsFieldProps) => (
  <div data-tour={dataTour} className={cn("min-w-0", className)}>
    <div className="mb-3">
      <div className="text-[1.5rem] font-semibold text-text">{label}</div>
      {hint && <div className="text-small text-text-tertiary">{hint}</div>}
    </div>
    {children}
  </div>
);

interface SettingsRowProps {
  label: ReactNode;
  hint?: string;
  children: ReactNode;
  className?: string;
}

/** A label on the left with its control on the right. Stack inside a
 * `divide-y` container to get a line between each row. */
export const SettingsRow = ({
  label,
  hint,
  children,
  className,
}: SettingsRowProps) => (
  <div
    className={cn(
      "flex flex-col gap-3 py-5 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between sm:gap-8",
      className
    )}
  >
    <div className="min-w-0">
      <div className="text-[1.5rem] font-medium text-text">{label}</div>
      {hint && <div className="text-small text-text-tertiary">{hint}</div>}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

export default SettingsCard;
